import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ActionButton } from "@/components/shared";
import { COUNTRIES } from "@/lib/countries";
import CitizenshipSelect from "@/components/CitizenshipSelect";

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

/**
 * Shared staff contact editor used by both the Lead Workspace Contact Info card and the
 * CRM Record Contact tab. It renders and change-tracks canonical contact fields and saves
 * through the single authoritative CRM update path (`PATCH /crm/contacts/:id`). It does NOT
 * own normalization / phone-preference / address-formatting / validation authority — those
 * remain backend responsibilities (contact_fields.py).
 */
export default function StaffContactEditor({ contact: c, clientId, onSaved, onCancel, extraFields = [], testidPrefix = "contact-edit" }) {
  const addr0 = c.mailing_address_structured || {};
  const seed = {
    first_name: c.first_name || "",
    last_name: c.last_name || "",
    email: c.email || "",
    mobile_phone: c.mobile_phone || c.mobile || "",
    home_phone: c.home_phone || "",
    whatsapp: c.whatsapp || "",
    line_id: c.line_id || "",
    country_of_residence: c.country_of_residence || "",
    planned_country_of_residence: c.planned_country_of_residence || "",
    line1: addr0.line1 || "", line2: addr0.line2 || "", city: addr0.city || "",
    region: addr0.region || "", postal_code: addr0.postal_code || "", country: addr0.country || "",
    citizenships: Array.isArray(c.citizenships) ? c.citizenships : [],
    ...Object.fromEntries(extraFields.map((f) => [f.key, c[f.key] ?? f.default ?? ""])),
  };
  const [initial] = useState(seed);
  const [form, setForm] = useState(seed);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const ADDR_KEYS = ["line1", "line2", "city", "region", "postal_code", "country"];
  const SCALAR_KEYS = ["first_name", "last_name", "email", "mobile_phone", "home_phone", "whatsapp", "line_id",
    "country_of_residence", "planned_country_of_residence",
    ...extraFields.map((f) => f.key)];

  const buildPayload = () => {
    const payload = {};
    SCALAR_KEYS.forEach((k) => {
      const cur = typeof form[k] === "string" ? form[k].trim() : form[k];
      const base = typeof initial[k] === "string" ? initial[k].trim() : initial[k];
      if ((cur ?? "") !== (base ?? "")) payload[k] = cur;
    });
    const addrChanged = ADDR_KEYS.some((k) => (form[k] || "").trim() !== (initial[k] || "").trim());
    if (addrChanged) {
      payload.mailing_address_structured = ADDR_KEYS.reduce((o, k) => {
        const v = (form[k] || "").trim();
        if (v) o[k] = v;
        return o;
      }, {});
    }
    const normCitz = (arr) => (arr || []).map((e) => ({ country_code: e.country_code, is_primary: !!e.is_primary }));
    if (JSON.stringify(normCitz(form.citizenships)) !== JSON.stringify(normCitz(initial.citizenships))) {
      payload.citizenships = normCitz(form.citizenships);
    }
    return payload;
  };

  const save = async () => {
    const e = {};
    if (!(form.first_name || "").trim()) e.first_name = "First name is required.";
    if (form.email && !isEmail(form.email.trim())) e.email = "Enter a valid email address.";
    if (Object.keys(e).length) { setErrors(e); toast.error("Please review the highlighted fields."); return; }
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { onCancel?.(); return; }
    setSaving(true);
    try {
      await api.patch(`/crm/contacts/${clientId}`, payload);
      toast.success("Contact information updated.");
      await onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error("You do not have permission to edit this contact.");
      else if (status === 409) toast.error("This contact changed while you were editing it. Refresh and try again.");
      else if (status === 400 || status === 422) toast.error("Please review the highlighted fields.");
      else toast.error("We could not save these changes. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField label="First Name" required testid={`${testidPrefix}-first-name`} value={form.first_name} onChange={set("first_name")} error={errors.first_name} />
        <TextField label="Last Name" testid={`${testidPrefix}-last-name`} value={form.last_name} onChange={set("last_name")} />
      </div>
      <TextField label="Email" testid={`${testidPrefix}-email`} type="email" value={form.email} onChange={set("email")} error={errors.email} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField label="Mobile Phone" testid={`${testidPrefix}-mobile-phone`} type="tel" value={form.mobile_phone} onChange={set("mobile_phone")} placeholder="+66 81 234 5678" />
        <TextField label="Home Phone" testid={`${testidPrefix}-home-phone`} type="tel" value={form.home_phone} onChange={set("home_phone")} placeholder="+1 555 123 4567" />
      </div>

      <fieldset className="rounded-lg border border-slate-200 p-3">
        <legend className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-1">Mailing Address</legend>
        <div className="space-y-3">
          <TextField label="Address Line 1" testid={`${testidPrefix}-line1`} value={form.line1} onChange={set("line1")} />
          <TextField label="Address Line 2" testid={`${testidPrefix}-line2`} value={form.line2} onChange={set("line2")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="City" testid={`${testidPrefix}-city`} value={form.city} onChange={set("city")} />
            <TextField label="State / Province / Region" testid={`${testidPrefix}-region`} value={form.region} onChange={set("region")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Postal Code" testid={`${testidPrefix}-postal-code`} value={form.postal_code} onChange={set("postal_code")} />
            <SelectField label="Country" testid={`${testidPrefix}-country`} value={form.country} onChange={set("country")} placeholder="Select country" options={COUNTRIES.map((x) => ({ value: x, label: x }))} />
          </div>
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField label="WhatsApp" testid={`${testidPrefix}-whatsapp`} value={form.whatsapp} onChange={set("whatsapp")} />
        <TextField label="LINE" testid={`${testidPrefix}-line`} value={form.line_id} onChange={set("line_id")} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SelectField label="Current Country of Residence" testid={`${testidPrefix}-country-of-residence`} value={form.country_of_residence} onChange={set("country_of_residence")} placeholder="Select country" options={COUNTRIES.map((x) => ({ value: x, label: x }))} />
        <SelectField label="Planned / Intended Residence" testid={`${testidPrefix}-planned-country`} value={form.planned_country_of_residence} onChange={set("planned_country_of_residence")} placeholder="Select country (optional)" options={COUNTRIES.map((x) => ({ value: x, label: x }))} />
      </div>

      <fieldset className="rounded-lg border border-slate-200 p-3">
        <legend className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-1">Citizenship(s)</legend>
        <CitizenshipSelect value={form.citizenships} onChange={set("citizenships")} testidPrefix={`${testidPrefix}-citizenship`} />
      </fieldset>

      {extraFields.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {extraFields.map((f) => (
            <SelectField key={f.key} label={f.label} testid={`${testidPrefix}-${f.key}`} value={form[f.key]} onChange={set(f.key)} options={f.options} placeholder={f.placeholder} />
          ))}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <ActionButton variant="secondary" onClick={onCancel} disabled={saving} data-testid={`${testidPrefix}-cancel`}>Cancel</ActionButton>
        <ActionButton onClick={save} loading={saving} disabled={saving} data-testid={`${testidPrefix}-save`}>Save Changes</ActionButton>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, error, type = "text", placeholder, testid, required }) {
  const errId = error ? `${testid}-error` : undefined;
  return (
    <div>
      <label htmlFor={testid} className="text-xs font-medium text-slate-500 mb-1 block">{label}{required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}</label>
      <input id={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid} aria-invalid={!!error} aria-describedby={errId} aria-label={label} aria-required={required || undefined}
        className={cn("w-full rounded-lg border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold",
          error ? "border-rose-400" : "border-slate-300")} />
      {error && <p id={errId} className="text-xs text-rose-600 mt-1" role="alert">{error}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options = [], placeholder, testid }) {
  return (
    <div>
      <label htmlFor={testid} className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>
      <select id={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} aria-label={label}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
